import { useEffect, useState } from 'react'
import { useAuthStore, useOrderStore, useRestaurantStore, OrderSchema, orderFromJson } from '@restaurant/core'
import { apiClient, isAuthExpiredError, isNotFoundError } from '@restaurant/api-client'
import type { Order } from '@restaurant/core'
import { safeGetLogger } from '@restaurant/logging'
import { isShowcase } from '../showcase/showcase'


/**
 * Hook to fetch and populate order history from Flask API
 * Fetches orders for the authenticated user and populates the orderStore
 * Endpoint: GET /orders/user/{userId}/business/{businessId}
 */
export function useOrderHistoryData() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { user, isAuthenticated } = useAuthStore()
  const { currentRestaurant } = useRestaurantStore()
  const { setOrderHistory } = useOrderStore()

  useEffect(() => {
    if (isShowcase()) return
    const fetchOrderHistory = async () => {
      if (!isAuthenticated || !user?.id) {
        safeGetLogger()?.debug('User not authenticated, skipping order history', { domain: 'order' })
        return
      }

      if (!currentRestaurant?.id) {
        safeGetLogger()?.debug('No restaurant/business ID, skipping', { domain: 'order' })
        return
      }

      const userId = user.id
      const businessId = currentRestaurant.id

      safeGetLogger()?.debug('Fetching order history', { domain: 'order', userId, businessId })

      setIsLoading(true)
      setError(null)

      try {
        // Call Flask endpoint: GET /orders/user/{userId}/business/{businessId}
        // The endpoint is PAGED: at most 5 orders per call, older pages via the
        // `last_order_time` cursor (orders/order_repository.py .limit(5)). Walk
        // the cursor until a short page or the cap, or users with >5 orders
        // silently lose history (support ticket 8c2692a1).
        const PAGE_SIZE = 5
        const MAX_ORDERS = 50
        const response: Array<{ order_id: string; order_time?: string }> = []
        let cursor: string | undefined
        while (response.length < MAX_ORDERS) {
          const query = cursor ? `?last_order_time=${encodeURIComponent(cursor)}` : ''
          const page = await apiClient.get<Array<{ order_id: string; order_time?: string }>>(
            `/orders/user/${userId}/business/${businessId}${query}`
          )
          if (!Array.isArray(page) || page.length === 0) break
          response.push(...page)
          const lastOrderTime = page[page.length - 1]?.order_time
          if (page.length < PAGE_SIZE || !lastOrderTime) break
          cursor = lastOrderTime
        }

        safeGetLogger()?.debug('Order IDs received', { domain: 'order', count: response.length })

        // The endpoint returns order IDs, we need to fetch full order details
        // For now, we'll create minimal order objects with the IDs
        // In a full implementation, you'd call getOrderDetails for each
        const orders: Order[] = []

        if (Array.isArray(response)) {
          for (const orderRef of response) {
            try {
              // Fetch full order details
              const orderDetails = await apiClient.get<unknown>(`/orders/${orderRef.order_id}/order_details`)
              // Use the shared orderFromJson mapper for consistent snake→camel deserialization
              const mapped = OrderSchema.parse(orderFromJson(orderDetails))
              orders.push(mapped)
            } catch (detailErr) {
              // Dev-only fallback — mirrors apps/mobile/src/hooks/useOrderHistoryData.ts.
              // See that file for the full rationale (test 8.1 / seeded order 401s).
              if (import.meta.env.DEV) {
                try {
                  const mapped = OrderSchema.parse(orderFromJson(orderRef))
                  orders.push(mapped)
                  safeGetLogger()?.debug('Used list-summary fallback for order', { domain: 'order', orderId: orderRef.order_id })
                  continue
                } catch (fallbackErr) {
                  safeGetLogger()?.warn('Fallback parse failed', { domain: 'order', orderId: orderRef.order_id, error: String(fallbackErr) })
                }
              }
              // A 404 here is expected and client-recoverable: the list endpoint
              // returned an order_id the detail call can't resolve (e.g. a
              // since-deleted/reaped order). The order is already skipped; log at
              // debug so it doesn't read as an error in the VPS log briefing.
              // Genuinely unexpected failures still warn.
              if (isNotFoundError(detailErr)) {
                safeGetLogger()?.debug('Failed to fetch order details', { domain: 'order', orderId: orderRef.order_id, error: String(detailErr) })
              } else {
                safeGetLogger()?.warn('Failed to fetch order details', { domain: 'order', orderId: orderRef.order_id, error: String(detailErr) })
              }
            }
          }
        }

        safeGetLogger()?.debug('Orders loaded', { domain: 'order', count: orders.length })

        // Update the order store with fetched orders
        setOrderHistory(orders)

      } catch (err) {
        if (isAuthExpiredError(err)) {
          // Auth refresh already failed; user is logged out. Don't surface a fetch
          // error to the UI — the auth flow will redirect/clear state.
          safeGetLogger()?.warn('Order history load failed — auth expired', { domain: 'order' })
        } else {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load order history'
          safeGetLogger()?.error('Order history load failed', { domain: 'order', error: errorMessage })
          setError(errorMessage)
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchOrderHistory()
  }, [isAuthenticated, user?.id, currentRestaurant?.id, setOrderHistory])

  return { isLoading, error }
}
